// Browser chats, in Settings' Endpoints group: which chat sites Origin records
// in people's browsers. Turning a site on saves at once. Turning one off says
// what stops and asks once. Every save carries the policy's revision, so a
// change another administrator made in the meantime is shown, not lost.
import { useEffect, useState } from "react";
import { authedFetch, restGet } from "../lib/restClient.js";

import { hasPermission, useMyPermissions } from "../lib/permissions.js";
import { PERMISSIONS } from "../lib/permissionKeys.js";
import { DIALOG } from "../lib/analytics/surfaceVocabulary.js";
import ConfirmDialog from "./ConfirmDialog.jsx";
import { AgentIcon } from "./Dimension.jsx";
import Button from "./ui/Button.jsx";
import Notice, { ErrorText } from "./ui/Notice.jsx";
import { SkeletonBar } from "./ui/Skeleton.jsx";
import Switch from "./ui/Switch.jsx";

const PROVIDERS = [
    ["openai", "ChatGPT", "chatgpt.com"],
    ["anthropic", "Claude", "claude.ai"],
    ["microsoft", "Microsoft Copilot", "copilot.microsoft.com"],
    ["perplexity", "Perplexity", "perplexity.ai"],
    ["deepseek", "DeepSeek", "chat.deepseek.com"],
    ["google", "Gemini", "gemini.google.com"],
];
const PATH = "/v1/tenant/browser-capture-policy";
const ROW = "flex min-h-[48px] min-w-0 items-center gap-[12px] border-b border-[color:var(--line-soft)] py-[8px] last:border-b-0";

// A site's name and address, the same while loading and loaded.
function Site({ label, url, dim = false }) {
    return (
        <>
            <AgentIcon name={label} className={`h-[16px] w-[16px] shrink-0 ${dim ? "opacity-50" : ""}`} />
            <span className="grid min-w-0 flex-1 gap-[1px]">
                <span className={`text-[13px] leading-[1.3] ${dim ? "text-[color:var(--muted)]" : "text-[color:var(--ink)]"}`}>{label}</span>
                <span className="break-words font-mono text-[12px] leading-[1.3] text-[color:var(--muted-soft)]">{url}</span>
            </span>
        </>
    );
}

export default function BrowserCaptureSettings() {
    const caps = useMyPermissions();
    const canWrite = hasPermission(caps, PERMISSIONS.TENANT_CONFIG_WRITE);
    const [policy, setPolicy] = useState(null);
    const [busy, setBusy] = useState(true);
    const [error, setError] = useState("");
    const [saved, setSaved] = useState("");
    // The site the reader asked to stop recording, waiting for them to confirm.
    const [asking, setAsking] = useState(null);

    const disabled = policy ? (policy.disabledProviderTags ?? policy.disabled_provider_tags ?? []) : [];
    const askingSite = PROVIDERS.find(([tag]) => tag === asking) ?? null;

    async function load() {
        setBusy(true);
        setError("");
        try { setPolicy(await restGet(PATH)); }
        catch { setError("Could not load the browser chat settings. Try again."); }
        finally { setBusy(false); }
    }

    useEffect(() => { void load(); }, []);

    async function save(nextDisabled, message) {
        setBusy(true);
        setError("");
        setSaved("");
        setAsking(null);
        try {
            const response = await authedFetch(PATH, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ expected_revision: policy.revision ?? "0", disabled_provider_tags: nextDisabled }),
            });
            if (response.status === 409) {
                setPolicy(await restGet(PATH));
                setError("Another administrator changed these settings, so yours wasn't saved. This is what they saved.");
                return;
            }
            if (!response.ok) throw new Error("save_failed");
            setPolicy(await response.json());
            setSaved(message);
        } catch { setError("Could not save the browser chat settings. Try again."); }
        finally { setBusy(false); }
    }

    function turn(tag, label, on) {
        if (on) save(disabled.filter((item) => item !== tag), `Saved. Origin records ${label} chats again as endpoints check in; offline ones pick it up when they reconnect.`);
        else { setSaved(""); setAsking(tag); }
    }

    return (
        <section id="browser-capture" aria-busy={busy} className="grid max-w-[640px] gap-[12px]">
            {/* While the policy loads, each site's row stands in with a placeholder
                for its switch: no guessed on/off state, and nothing moves when it lands. */}
            {!policy && busy ? (
                <div className="grid min-w-0">
                    <span className="sr-only">Loading the browser chat settings…</span>
                    {PROVIDERS.map(([tag, label, url]) => (
                        <div key={tag} className={ROW}>
                            <Site label={label} url={url} dim />
                            <SkeletonBar className="w-[30px] shrink-0" />
                        </div>
                    ))}
                </div>
            ) : null}
            {!policy && !busy ? (
                <Notice tone="error" action={<Button size="sm" onClick={load}>Retry</Button>}>{error}</Notice>
            ) : null}
            {policy ? (
                <fieldset disabled={busy || !canWrite} className="m-0 grid min-w-0 border-0 p-0">
                    <legend className="sr-only">Chat sites Origin records</legend>
                    {PROVIDERS.map(([tag, label, url]) => {
                        const on = !disabled.includes(tag);
                        return (
                            <div key={tag} className={ROW}>
                                <Site label={label} url={url} />
                                <Switch checked={on && asking !== tag} disabled={busy || !canWrite} label={`Record ${label} chats`} onChange={(next) => turn(tag, label, next)} />
                            </div>
                        );
                    })}
                </fieldset>
            ) : null}
            {policy ? <ErrorText>{error}</ErrorText> : null}
            {saved ? <p role="status" className="m-0 text-[12px] leading-[1.4] text-[color:var(--muted)]">{saved}</p> : null}
            <ConfirmDialog
                open={askingSite != null}
                analyticsId={DIALOG.BROWSER_CAPTURE_STOP}
                eyebrow="Browser extension"
                title={askingSite ? `Stop recording ${askingSite[1]} chats?` : ""}
                body={askingSite ? `Origin stops recording ${askingSite[1]} chats on ${askingSite[2]} for everyone, from each endpoint's next check-in. Chats it already recorded stay.` : null}
                confirmLabel="Stop recording"
                cancelLabel="Keep recording"
                destructive
                confirmDisabled={busy}
                onConfirm={() => askingSite && save([...disabled, askingSite[0]], `Saved. Origin stops recording ${askingSite[1]} chats as endpoints check in.`)}
                onCancel={() => setAsking(null)}
            />
        </section>
    );
}
